/**
 * useCostPreview — pre-Run cost preview for the analysis composer (PRI-488 F1).
 *
 * Dogfoods the public API's dry-run + pricing surface:
 *   * POST /v1/analyses {validate_only: true} via sdk.analyses.validateOnly()
 *     when the composer knows the public video id (fresh uploads return
 *     user_videos.public_id from POST /v1/user-videos).
 *   * GET /v1/credit-pricing (public, no auth) for the credit rate.
 *
 * When no public video id is resolvable (e.g. legacy library rows loaded from
 * the internal list endpoint, which does not shape public_id), the preview
 * falls back to a client-side estimate using the SAME formula the server
 * applies (ceil(duration) seconds × price_per_second_cents) and the already-
 * parsed compiled query for assessability — so the number can't drift from
 * what validate_only would report.
 *
 * Zero credits are ever spent by this hook: validate_only never creates an
 * analysis, and credit-pricing is a public config read.
 */
import { useEffect, useRef, useState } from 'react'
import { sdk } from '../../core/lib/api/sdk'
import type { CompiledQuery } from '../../core/components/v2/compiled-query-types'
import type { CostPreviewData } from '../../core/components/v2/pv2-shared'

export type { CostPreviewData }

const REPHRASE_HINT =
  'This prompt can\u2019t be scored \u2014 rephrase as a yes/no or count question'

/**
 * Pure preview computation — mirrors the server's validate_only math:
 * assessable = query_type present and not open_ended;
 * estimated_seconds = ceil(video duration).
 * Exported for unit tests.
 */
export function computeCostPreview(params: {
  queryType: string | null | undefined
  durationS: number | null
  balanceSeconds: number | null
  source: 'server' | 'client'
  /** Server-provided values (validate_only response) override local math. */
  serverAssessable?: boolean
  serverEstimatedSeconds?: number | null
}): Omit<CostPreviewData, 'loading'> {
  const assessable = params.serverAssessable !== undefined
    ? params.serverAssessable
    : (params.queryType != null && params.queryType !== '' && params.queryType !== 'open_ended')
  const estimatedSeconds = params.serverEstimatedSeconds !== undefined && params.serverEstimatedSeconds !== null
    ? params.serverEstimatedSeconds
    : (params.durationS != null && params.durationS > 0 ? Math.ceil(params.durationS) : null)
  const balanceAfter = params.balanceSeconds != null && estimatedSeconds != null
    ? params.balanceSeconds - estimatedSeconds
    : null
  return {
    assessable,
    estimatedSeconds: assessable ? estimatedSeconds : null,
    balanceSeconds: params.balanceSeconds,
    balanceAfter: assessable ? balanceAfter : null,
    insufficient: assessable && balanceAfter != null && balanceAfter < 0,
    rephraseHint: assessable ? null : REPHRASE_HINT,
    source: params.source,
  }
}

/** Probe a local video File/Blob for its duration in seconds (null on failure). */
export function getVideoFileDuration(file: File | Blob): Promise<number | null> {
  return new Promise(resolve => {
    try {
      const url = URL.createObjectURL(file)
      const video = document.createElement('video')
      video.preload = 'metadata'
      const done = (d: number | null) => {
        URL.revokeObjectURL(url)
        resolve(d != null && Number.isFinite(d) && d > 0 ? d : null)
      }
      video.onloadedmetadata = () => done(video.duration)
      video.onerror = () => done(null)
      video.src = url
    } catch {
      resolve(null)
    }
  })
}

const DEBOUNCE_MS = 400

export function useCostPreview(params: {
  /** Master gate — false renders nothing and fires no requests. */
  enabled: boolean
  /** Compiled query from the existing debounced /v1/parse hook. */
  compiledQuery: CompiledQuery | null
  /** Public video id (video_…) when known — unlocks the server dry-run. */
  publicVideoId: string | null
  /** Client-side known duration (file metadata / library row), seconds. */
  durationS: number | null
  /** Account balance from the quota bar (credit-seconds). */
  balanceSeconds: number | null
}): CostPreviewData | null {
  const { enabled, compiledQuery, publicVideoId, durationS, balanceSeconds } = params
  const [preview, setPreview] = useState<CostPreviewData | null>(null)
  const genRef = useRef(0)

  useEffect(() => {
    if (!enabled || !compiledQuery) {
      setPreview(null)
      return
    }
    const gen = ++genRef.current
    const queryType = (compiledQuery as { query_type?: string }).query_type ?? null

    // Immediate client-side estimate (no network) so the UI never blocks.
    setPreview({
      loading: publicVideoId != null,
      ...computeCostPreview({ queryType, durationS, balanceSeconds, source: 'client' }),
    })

    if (!publicVideoId) return

    // Debounced server dry-run: POST /v1/analyses {validate_only: true}.
    // Sends the pre-compiled query (parse_mode client) so the server does NOT
    // re-parse the prompt — zero duplicate LLM calls, zero credits.
    const timer = setTimeout(() => {
      sdk.analyses
        .validateOnly({ video_id: publicVideoId, query: compiledQuery as unknown as Record<string, unknown> })
        .then(p => {
          if (genRef.current !== gen) return
          setPreview({
            loading: false,
            ...computeCostPreview({
              queryType,
              durationS,
              balanceSeconds,
              source: 'server',
              serverAssessable: p.assessable,
              serverEstimatedSeconds: p.estimated_seconds,
            }),
          })
        })
        .catch(() => {
          if (genRef.current !== gen) return
          // Server dry-run unavailable (network, legacy row, race with upload)
          // — keep the client estimate, just clear the loading flag.
          setPreview(prev => (prev ? { ...prev, loading: false } : prev))
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [enabled, compiledQuery, publicVideoId, durationS, balanceSeconds])

  return enabled ? preview : null
}
